import {DateTime} from '../services/dateTime';
import React, { useEffect, useState, useRef } from "react";
import {usePhotoNavigationGuard} from '../services/photos/usePhotoNavigationGuard';
import {Link} from 'react-router-dom';
import { useAuth } from "../services/authContext";
import { useGuest } from "../services/GuestContext";
import { contributionRequest, WorkState } from "../services/contributionApi";
import { WorkLog, deriveWorkLogDurationMs } from "../types";

export function WorkCorrection({
  work,
  farmId,
  onSaved,
}: {
  work: WorkLog;
  farmId?: string;
  onSaved: () => void;
}) {
  const { farm } = useGuest();
  const pending=useRef<Record<string,unknown>|null>(null);
  const [busy,setBusy]=useState(false);
  const [open, setOpen] = useState(false),
    [intervals, setIntervals] = useState(work.intervals || []),
    [reason, setReason] = useState(""),
    [error, setError] = useState("");
  usePhotoNavigationGuard(open||busy);
  if(work.entry_kind==='manual')return <Link className="inline-flex min-h-11 items-center text-sm underline" to={`/projects/${work.project_id}`}>Open project to correct this manual entry</Link>;
  const local = (v: string) => {
    const d = new Date(v);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 19);
  };
  return (
    <div>
      {!open ? (
        <button className="underline mt-2" onClick={() => setOpen(true)}>
          Correct work
        </button>
      ) : (
        <div className="space-y-3 mt-3">
          {intervals.map((i, n) => (
            <div className="grid sm:grid-cols-2 gap-2" key={n}>
              {(["start", "end"] as const).map((k) => (
                <label key={k} className="text-sm">
                  {k === "start" ? "Work started" : "Work ended"}
                  <input
                    className="border p-2 rounded w-full"
                    type="datetime-local"
                    step={1}
                    value={i[k] ? local(i[k]!) : ""}
                    onChange={(e) => {
                      const next = [...intervals];
                      next[n] = {
                        ...i,
                        [k]: e.target.value
                          ? new Date(e.target.value).toISOString()
                          : null,
                      };
                      setIntervals(next);
                    }}
                  />
                </label>
              ))}
              <button
                className="underline text-sm"
                onClick={() =>
                  setIntervals(intervals.filter((_, index) => index !== n))
                }
              >
                Remove interval
              </button>
            </div>
          ))}
          <button
            className="underline"
            onClick={() =>
              setIntervals([
                ...intervals,
                {
                  start: new Date(Date.now() - 60000).toISOString(),
                  end: new Date().toISOString(),
                },
              ])
            }
          >
            Add active interval
          </button>
          <p className="text-sm">
            Duration to review:{" "}
            {Math.floor(deriveWorkLogDurationMs({ ...work, intervals }) / 1000)}{" "}
            seconds
          </p>
          <label className="block text-sm">
            Correction reason
            <input
              className="border p-2 rounded w-full"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
            />
          </label>
          {error && <p role="alert">{error}</p>}
          <button
            disabled={!reason.trim()||busy}
            className="border rounded px-3 py-2"
            onClick={() =>
              void (async () => {
                try {
                  setBusy(true);
                  pending.current ||= {
                    operation_id: crypto.randomUUID(),
                    command: "correct",
                    id: work.id,
                    expected_revision: work.revision,
                    intervals,
                    reason,
                  };
                  await contributionRequest(farmId||farm!.farm_id, "/work", "POST", pending.current);
                  pending.current=null;
                  setOpen(false);
                  onSaved();
                } catch (e) {
                  setError((e as Error).message);
                  if([400,401,403,404,409].includes((e as any).status))pending.current=null;
                } finally {
                  setBusy(false);
                }
              })()
            }
          >
            {pending.current?'Retry correction':'Save correction'}
          </button>
          <button className="ml-3 underline" disabled={busy||!!pending.current} onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
type Photo = {
  photo_id: string;
  created_by: string;
  uploader_email?: string;
  revision?: number;
  excluded?: boolean;
  deleted_at?: string;
  digest?: string;
  source?: string;
};
type Unresolved = {
  projects: { id: string; name: string }[];
  work: { id: string; project_id: string; user_id: string }[];
  photos: Photo[];
};
export default function ContributionReview() {
  const { privileges, user } = useAuth(),
    { farm } = useGuest();
  const [state, setState] = useState<WorkState | null>(null),
    [photos, setPhotos] = useState<Photo[]>([]),
    [unresolved, setUnresolved] = useState<Unresolved | null>(null),
    [members, setMembers] = useState<{ email: string; name: string }[]>([]);
  const [reason, setReason] = useState(""),
    [error, setError] = useState(""),
    [working, setBusy] = useState(false),
    [selected, setSelected] = useState(""),
    [contributor, setContributor] = useState(""),
    [preview, setPreview] = useState<{
      photo_id: string;
      digest: string;
    } | null>(null);
  const [start, setStart] = useState(""),
    [end, setEnd] = useState("");
  const [recordProject, setRecordProject] = useState("");
  const [history, setHistory] = useState<
    Record<
      string,
      {
        revisions: { revision: number; reason: string; actor_email: string }[];
        reviews: {
          review_id: number;
          revision: number;
          decision: string;
          reason: string;
          reviewer_email: string;
        }[];
      }
    >
  >({});
  const [extraIntervals, setExtraIntervals] = useState<
    { start: string; end: string }[]
  >([]);
  const allowed =
    privileges.contributionReview?.view || privileges.contributionReview?.write;
  const busy =
    working || !privileges.contributionReview?.write || !state?.can_review;
  const load = async () => {
    if (!farm || !allowed) return;
    const [work, photo, unknown, people] = await Promise.all([
      contributionRequest<WorkState>(farm.farm_id),
      contributionRequest<{ photos: Photo[] }>(farm.farm_id, "/photos"),
      contributionRequest<Unresolved>(farm.farm_id, "/unresolved"),
      contributionRequest<{ members: typeof members }>(
        farm.farm_id,
        "/members",
      ),
    ]);
    const source = window.location.hash.startsWith("#work-")
      ? window.location.hash.slice(6)
      : "";
    if (source && !work.work.some((w) => w.id === source))
      work.work.push(
        (
          await contributionRequest<{ work: WorkLog }>(
            farm.farm_id,
            "/work/" + encodeURIComponent(source),
          )
        ).work,
      );
    setState(work);
    setPhotos(photo.photos);
    setUnresolved(unknown);
    setMembers(people.members);
  };
  useEffect(() => {
    setHistory({});
    setState(null);
    setPhotos([]);
    setUnresolved(null);
    setMembers([]);
    if (allowed) void load().catch((e) => setError(e.message));
  }, [farm?.farm_id, user?.email, allowed]);
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView({ block: "center" });
  }, [state]);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const post = (path: string, body: unknown) =>
    contributionRequest(farm!.farm_id, path, "POST", {
      operation_id: crypto.randomUUID(),
      ...(body as object),
    });
  if (!allowed || !farm) return null;
  return (
    <section className="max-w-4xl mx-auto space-y-4" id="contribution-review">
      <h2 className="text-xl font-semibold">Work &amp; patch credit</h2>
      <p className="text-sm text-gray-600">Approve recorded work before it counts toward patches. Photo credit and older attribution corrections are available below; they do not publish photos.</p>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      <label className="block text-sm">
        Review or correction reason
        <textarea
          value={reason}
          maxLength={500}
          onChange={(e) => setReason(e.target.value)}
          className="block w-full p-2 border rounded mt-1"
        />
      </label>
      {!state && !error && <p role="status" className="text-sm text-gray-500">Loading contributions…</p>}
      {state && !state.work.some(w => w.state === "stopped" && w.contributor_email) && <p className="text-sm text-gray-500">No recorded work to review.</p>}
      <div className="space-y-3">
        {state?.work
          .filter((w) => w.state === "stopped" && w.contributor_email)
          .map((w) => (
            <article
              className="border rounded-xl p-3 space-y-2"
              key={w.id}
              id={`work-${w.id}`}
            >
              <p className="font-medium">
                {(w as any).contributor_name} · {(w as any).project_name}
              </p>
              <p className="text-sm">
                {w.worked_on?.slice(0,10)||(w.start_time?new Date(w.start_time).toLocaleDateString():'')} ·{" "}
                {Math.floor(deriveWorkLogDurationMs(w) / 1000)} active seconds ·{" "}
                {w.review_status}
              </p>
              <details className="text-sm">
                <summary>Active intervals</summary>
                {w.intervals?.map((i, n) => (
                  <p key={n}>
                    {<DateTime value={i.start}/>} —{" "}
                    {i.end && <DateTime value={i.end}/>}
                  </p>
                ))}
              </details>
              <div className="flex flex-wrap gap-3">
                {(["approve", "return", "invalidate"] as const).map(
                  (decision) => (
                    <button
                      disabled={
                        busy ||
                        (decision !== "approve" && !reason.trim()) ||
                        (w.contributor_email === user?.email && !reason.trim())
                      }
                      className="border px-3 py-2 rounded disabled:opacity-50"
                      key={decision}
                      onClick={() =>
                        void run(() =>
                          post("/review/" + encodeURIComponent(w.id), {
                            decision,
                            expected_revision: w.revision,
                            reason,
                          }),
                        )
                      }
                    >
                      {decision === "approve"
                        ? "Approve"
                        : decision === "return"
                          ? "Return for changes"
                          : "Invalidate"}
                    </button>
                  ),
                )}
              </div>
              <details
                className="text-sm"
                onToggle={(e) => {
                  if (e.currentTarget.open && !history[w.id])
                    void contributionRequest<{
                      revisions: {
                        revision: number;
                        reason: string;
                        actor_email: string;
                      }[];
                      reviews: {
                        review_id: number;
                        revision: number;
                        decision: string;
                        reason: string;
                        reviewer_email: string;
                      }[];
                    }>(farm.farm_id, "/work/" + encodeURIComponent(w.id))
                      .then((result) =>
                        setHistory((current) => ({
                          ...current,
                          [w.id]: result,
                        })),
                      )
                      .catch((e) => setError(e.message));
                }}
              >
                <summary>Review and correction history</summary>
                {history[w.id]?.revisions.map((r) => (
                  <p key={r.revision}>
                    Revision {r.revision} · {r.actor_email}
                    {r.reason ? " · " + r.reason : ""}
                  </p>
                ))}
                {history[w.id]?.reviews.map((r) => (
                  <p key={r.review_id}>
                    Revision {r.revision} · {r.decision} · {r.reviewer_email} ·{" "}
                    {r.reason}
                  </p>
                ))}
              </details>
            </article>
          ))}
      </div>
      <details>
        <summary className="cursor-pointer font-medium">
          Photo credit for patches
        </summary>
        <div className="space-y-3 mt-3">
          <p className="text-sm text-gray-600">Choose whether a verified photo counts toward photo contribution patches. This does not change its gallery visibility.</p>
          {!photos.length && state && <p className="text-sm text-gray-500">No photo credit records to review.</p>}
          {photos.map((p) => (
            <article
              key={p.photo_id}
              id={`photo-${p.photo_id}`}
              className="border rounded p-3 text-sm"
            >
              <a
                className="underline"
                href={"/gallery/" + encodeURIComponent(p.photo_id)}
              >
                Photo {p.photo_id}
              </a>
              <p>
                {p.uploader_email || p.created_by || "Attribution unresolved"} ·{" "}
                {p.deleted_at
                  ? "Deleted"
                  : p.excluded
                    ? "Excluded"
                    : "Available"}
              </p>
              {p.uploader_email && (
                <div className="flex flex-wrap gap-3 mt-2">
                  {[
                    p.excluded ? "include" : "exclude",
                  ].map((action) => (
                    <button
                      key={action}
                      className="border px-3 py-2 rounded"
                      disabled={busy || !reason.trim()}
                      onClick={() =>
                        void run(() =>
                          post("/photos/" + encodeURIComponent(p.photo_id), {
                            action,
                            expected_revision: p.revision,
                            reason,
                          }),
                        )
                      }
                    >
                      {action === "include" ? "Count toward patches" : "Exclude from patch credit"}
                    </button>
                  ))}
                </div>
              )}
              {p.uploader_email && (
                <div className="mt-3 space-y-2">
                  <button
                    className="underline"
                    disabled={busy}
                    onClick={() =>
                      void run(async () =>
                        setPreview(
                          (await post(
                            "/photos/" +
                              encodeURIComponent(p.photo_id) +
                              "/preview",
                            {},
                          )) as { photo_id: string; digest: string },
                        ),
                      )
                    }
                  >
                    Verify or correct attribution
                  </button>
                  {preview?.photo_id === p.photo_id && (
                    <>
                      <label className="block">
                        Uploader
                        <select
                          className="block border rounded p-2 w-full"
                          value={contributor}
                          onChange={(e) => setContributor(e.target.value)}
                        >
                          <option value="">Choose a member</option>
                          {members.map((m) => (
                            <option key={m.email} value={m.email}>
                              {m.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button
                        className="border rounded px-3 py-2"
                        disabled={busy || !contributor || !reason.trim()}
                        onClick={() =>
                          void run(() =>
                            post("/photos/" + encodeURIComponent(p.photo_id), {
                              action: "attribute",
                              expected_revision: p.revision,
                              uploader_email: contributor,
                              digest: preview.digest,
                              reason,
                            }),
                          )
                        }
                      >
                        Confirm verified attribution
                      </button>
                    </>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
      </details>
      <details>
        <summary className="cursor-pointer font-medium">
          Assign verified contributors to older records
        </summary>
        <div className="space-y-3 mt-3 text-sm">
          <p>
            {unresolved?.projects.length || 0} unresolved projects ·{" "}
            {unresolved?.work.length || 0} work logs ·{" "}
            {unresolved?.photos.length || 0} photos. Verify property ownership
            before assigning.
          </p>
          {user?.roles?.includes("Admin") &&
            unresolved?.projects.map((p) => (
              <div className="border p-3 rounded" key={p.id}>
                {p.name}
                <button
                  className="block underline mt-2"
                  disabled={busy || !reason.trim()}
                  onClick={() =>
                    void run(() =>
                      post(
                        "/projects/" + encodeURIComponent(p.id) + "/ownership",
                        { reason },
                      ),
                    )
                  }
                >
                  Confirm belongs to {farm.name}
                </button>
              </div>
            ))}
          <label className="block">
            Contributor
            <select
              className="block w-full border p-2 rounded"
              value={contributor}
              onChange={(e) => setContributor(e.target.value)}
            >
              <option value="">Choose a member</option>
              {members.map((m) => (
                <option value={m.email} key={m.email}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            Work log
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="block w-full border p-2 rounded"
            >
              <option value="">Record new work on behalf of member</option>
              {[
                ...(unresolved?.work || []),
                ...(state?.work.filter((w) => w.contributor_email) || []),
              ].map((w) => (
                <option value={w.id} key={w.id}>
                  {w.user_id} · {w.project_id}
                </option>
              ))}
            </select>
          </label>
          {!selected && (
            <label className="block">
              Project
              <select
                className="block w-full border p-2 rounded"
                value={recordProject}
                onChange={(e) => setRecordProject(e.target.value)}
              >
                <option value="">Choose a project</option>
                {state?.projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="grid sm:grid-cols-2 gap-2">
            <label>
              Active start
              <input
                className="block border rounded p-2 w-full"
                type="datetime-local"
                step={1}
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </label>
            <label>
              Active end
              <input
                className="block border rounded p-2 w-full"
                type="datetime-local"
                step={1}
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </label>
          </div>
          {extraIntervals.map((interval, index) => (
            <div className="grid sm:grid-cols-2 gap-2" key={index}>
              {(["start", "end"] as const).map((key) => (
                <label key={key}>
                  Active {key}
                  <input
                    className="block border rounded p-2 w-full"
                    type="datetime-local"
                    step={1}
                    value={interval[key]}
                    onChange={(e) =>
                      setExtraIntervals((rows) =>
                        rows.map((row, n) =>
                          n === index ? { ...row, [key]: e.target.value } : row,
                        ),
                      )
                    }
                  />
                </label>
              ))}
              <button
                className="underline"
                onClick={() =>
                  setExtraIntervals((rows) =>
                    rows.filter((_, n) => n !== index),
                  )
                }
              >
                Remove interval
              </button>
            </div>
          ))}
          <button
            className="underline"
            onClick={() =>
              setExtraIntervals((rows) => [...rows, { start: "", end: "" }])
            }
          >
            Add another active interval
          </button>
          <p>
            Attest only active time, excluding pauses. Duration:{" "}
            {Math.floor(
              [{ start, end }, ...extraIntervals].reduce(
                (sum, i) =>
                  sum +
                  (i.start && i.end
                    ? Math.max(0, Date.parse(i.end) - Date.parse(i.start))
                    : 0),
                0,
              ) / 1000,
            )}{" "}
            seconds.
          </p>
          <button
            className="border rounded px-3 py-2"
            disabled={
              busy ||
              (!selected && !recordProject) ||
              !contributor ||
              !start ||
              !end ||
              extraIntervals.some((i) => !i.start || !i.end) ||
              !reason.trim()
            }
            onClick={() =>
              void run(() =>
                post("/work", {
                  command: "attest",
                  id: selected || undefined,
                  project_id: selected ? undefined : recordProject,
                  contributor_email: contributor,
                  expected_revision:
                    state?.work.find((w) => w.id === selected)?.revision || 0,
                  intervals: [{ start, end }, ...extraIntervals].map((i) => ({
                    start: new Date(i.start).toISOString(),
                    end: new Date(i.end).toISOString(),
                  })),
                  reason,
                }),
              )
            }
          >
            Attest for review
          </button>
          {unresolved?.photos.map((p) => (
            <div key={p.photo_id} className="border rounded p-3">
              <p>
                {p.photo_id} · Original attribution: {p.created_by || "Unknown"}
              </p>
              <button
                className="underline"
                disabled={busy}
                onClick={() =>
                  void run(async () =>
                    setPreview(
                      (await post(
                        "/photos/" +
                          encodeURIComponent(p.photo_id) +
                          "/preview",
                        {},
                      )) as { photo_id: string; digest: string },
                    ),
                  )
                }
              >
                Verify stored original
              </button>
              {preview?.photo_id === p.photo_id && (
                <button
                  className="block border rounded px-3 py-2 mt-2"
                  disabled={busy || !contributor || !reason.trim()}
                  onClick={() =>
                    void run(() =>
                      post("/photos/" + encodeURIComponent(p.photo_id), {
                        action: "attribute",
                        uploader_email: contributor,
                        digest: preview.digest,
                        expected_revision: 0,
                        reason,
                      }),
                    )
                  }
                >
                  Confirm attribution
                </button>
              )}
            </div>
          ))}
        </div>
      </details>
    </section>
  );
}
